import { createContext, useEffect, useRef, useState } from 'react';
import Bottom from "./Components/Bottom/Bottom.tsx";
import { v4 as uuidv4 } from 'uuid';
import Middle from "./Components/Middle/Middle.tsx";
import Top from "./Components/Top/Top.tsx";
import {DEFAULT_MISC_MID_RENDER_REFS, DEFAULT_MODEL, getDefaultLlmRequestRefs, THEME_COLORS} from "./Globals.ts";
import {auth} from "./Components/MiddlePopup/SignInModal/firebaseConfig.ts";
import {useAppStore} from "./GlobalStore/GlobalStore.ts";
import {Tooltip} from "react-tooltip";
import messageIdMapping from "./Utils/IdMappings/MessageIdMapping.ts";
import MiddlePopup from "./Components/MiddlePopup/MiddlePopup.tsx";
import MenuPanel from "./Components/MenuPanel/MenuPanel.tsx";
import {setupWebsocket} from "./Utils/websocket/websocketUtils.ts";
import {isModelPremium} from "./Utils/misc/modelInsightUtils.ts";
import {isSubscribed} from "./Utils/misc/userInsightUtils.ts";
import {handleEndTurn} from "./Utils/llm/responseStream/receiver/responseFinish/finishResponse.ts";
import {handleSave} from "./Utils/db/chatHistory/saveFullChat.ts";
import {isMobileApp} from "./Utils/misc/envNameUtils.ts";
import {safeGetVscode, safeLocalStorage} from "./Utils/misc/miscUtils.ts";
import {setLoggedOutSettings, setUnpaidSettings, setupUserState} from "./Utils/db/users/setupUserState.ts";
import {getThemeForAuto} from "./Utils/misc/themeUtils.ts";
import {resetState} from "./Utils/misc/applicationStateUtils.ts";
import {getAllChats} from "./Utils/db/chatHistory/loadChat.ts";
import {handleMessage} from "./Utils/jsBridge/receiver/messageReceivedHandler.ts";
import {checkEmbeddingInfoForProject} from "./Utils/db/users/userInfo/checkEmbeddingInfoForProject.ts";
import {handleDragOver, handleDrop} from "./Utils/uploadUtils/helpers/dragAndDrop.ts";
import {stopRecording} from "./Components/Bottom/MediaInput/utils/recordingUtils.ts";
import {handleAcceptFile} from "./Components/Middle/Message/UnreviewedFiles/UnreviewedFiles.tsx";
import {handleSubmit, processSubmit, processMessageQueue} from "./Utils/llm/responseStream/sender/utils.ts";

export const GlobalContext = createContext({
    websocketRef: { current: null as any | null }, messagesRef: { current: null as any | null }, currentMessageRef: { current: null as any | null },
    messagesSequenceRef: { current: [] as number[] }, menuRef: { current: null as any | null }, editorRef: { current: null as any | null },
    userObjRef: { current: null as any | null }, settingsObjRef: { current: null as any | null },
    environmentObjRef: { current: null as any | null }, dynamicStateObjRef: { current: null as any | null },
    setMessages: null as any | null, llmRequestRefs: null as any | null, miscMidRenderRefs: { current: null as any | null },
    resetStateHelper: null as any | null, setupWebsocketHelper: null as any | null, handleSubmitHelper: null as any | null,
    processSubmitHelper: null as any | null, handleEndTurnHelper: null as any | null, stopRecordingHelper: null as any | null
});

declare global {
    interface Window {
        ReactNativeWebView: { postMessage(message: string): void; };
        backendMessage(functionName: string, data: any): void;
        jetbrainsPostMessage(message: string): void;
        acquireVsCodeApi(): any;
    }
}

function App() {
    const [messages, setMessages] = useState<any>(null);

    const websocketRef = useRef<any>(null);
    const menuRef = useRef<any>(null);
    const editorRef: any = useRef<any>(null);
    const messagesRef: any = useRef(null);
    const currentMessageRef = useRef(null);
    const messagesSequenceRef = useRef<number[]>([]);
    const userObjRef: any = useRef(null);
    const environmentObjRef: any = useRef(null);
    const settingsObjRef: any = useRef(null);
    // These refs are for variables that need to receive updates between renders (e.g. long running function needs interrupt)
    const miscMidRenderRefs = useRef(DEFAULT_MISC_MID_RENDER_REFS);
    // These are refs that get used in the actual LLM request. They need to be top level refs because the functions that use them stop and resume without a re-render (due to waiting for application to send back context)
    const llmRequestRefs = useRef<any>(getDefaultLlmRequestRefs());
    const isStoppingRef = useRef(false);

    const userObj = useAppStore((state: any) => state.userObj);
    const setUserObj = useAppStore((state: any) => state.setUserObj);
    const settingsObj = useAppStore((state: any) => state.settingsObj);
    const setSettingsObj = useAppStore((state: any) => state.setSettingsObj);
    const environmentObj = useAppStore((state: any) => state.environmentObj);
    const setEnvironmentObj = useAppStore((state: any) => state.setEnvironmentObj);
    const dynamicStateObj = useAppStore((state: any) => state.dynamicStateObj);
    const dynamicStateObjRef = useRef(dynamicStateObj);
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);
    const updateElementInProgress = useAppStore((state: any) => state.updateElementInProgress)
    const updateImportantMessageIds = useAppStore((state: any) => state.updateImportantMessageIds);
    const updateSessionUsage = useAppStore((state: any) => state.updateSessionUsage);
    const updateChatHistory = useAppStore((state: any) => state.updateChatHistory);
    const updateSelectedMedia = useAppStore((state: any) => state.updateSelectedMedia);
    const updateOpenFileStatuses = useAppStore((state: any) => state.updateOpenFileStatuses);
    const updateProjectFileBatchIds = useAppStore((state: any) => state.updateProjectFileBatchIds);
    const updateUnreviewedFileTracker = useAppStore((state: any) => state.updateUnreviewedFileTracker);
    const updateMessageQueue = useAppStore((state: any) => state.updateMessageQueue);

    const resetStateHelper = () => resetState(messagesRef, currentMessageRef, messagesSequenceRef, setDynamicStateObj, setMessages, miscMidRenderRefs, updateElementInProgress, updateImportantMessageIds);
    const setupWebsocketHelper = () => setupWebsocket(websocketRef, editorRef, llmRequestRefs, dynamicStateObjRef, settingsObjRef, userObjRef, setMessages, environmentObjRef, messagesRef, currentMessageRef, messagesSequenceRef, setUserObj, setDynamicStateObj, updateElementInProgress, updateSessionUsage, updateSelectedMedia, updateProjectFileBatchIds, stopRecordingHelper, miscMidRenderRefs, setEnvironmentObj, setSettingsObj);
    const processSubmitHelper = () => processSubmit(websocketRef, messagesRef, currentMessageRef, messagesSequenceRef, settingsObjRef, environmentObjRef, userObjRef, dynamicStateObjRef, llmRequestRefs, setupWebsocketHelper, setMessages, updateElementInProgress, setDynamicStateObj);
    const handleSubmitHelper = () => handleSubmit(setMessages, messagesRef, currentMessageRef, messagesSequenceRef, settingsObjRef, environmentObjRef, dynamicStateObjRef, setDynamicStateObj, updateElementInProgress, llmRequestRefs, processSubmitHelper);
    const handleEndTurnHelper = (wasAborted: boolean) => handleEndTurn(messagesSequenceRef, isStoppingRef, dynamicStateObjRef, setDynamicStateObj, setUserObj, setMessages, updateElementInProgress, userObjRef, environmentObjRef.current.environment, environmentObjRef.current.vscode, llmRequestRefs, settingsObjRef, websocketRef, wasAborted, messagesRef);

    const stopRecordingHelper = () => stopRecording(miscMidRenderRefs, dynamicStateObjRef, userObjRef, settingsObjRef, updateSelectedMedia, websocketRef, setupWebsocketHelper, environmentObjRef.current.environment, environmentObjRef.current.vscode, setDynamicStateObj);

    // Needed for listeners since they can't update with state variables. They get setup once and never again
    useEffect(() => {
        // If you sign in with a convo in progress, save the convo and start a new chat. Hacky, but works
        if (userObj.user && userObj.user.emailVerified && !userObjRef.current?.user && Object.keys(messageIdMapping).length !== 0) {
            handleSave(userObj.user, messagesSequenceRef, updateChatHistory, setDynamicStateObj).then(() => resetStateHelper());
        }
        if (websocketRef.current && websocketRef.current?.readyState === WebSocket.OPEN && userObj.user?.emailVerified && userObj.user.uid) {
            websocketRef.current?.send(JSON.stringify({
                functionName: "giveUserId",
                args: {userId: userObj.user.uid}
            }));
            if (isMobileApp(environmentObjRef.current?.environment)) {
                window.ReactNativeWebView.postMessage(JSON.stringify({
                    functionName: "giveUserId",
                    data: [{ userId: userObj.user.uid }]
                }));
            }
        }

        if (!dynamicStateObjRef.current?.isInitializing && userObj.monthlyFreeMessages <= 0) {
            if (userObj.subscriptionPlan === "hybrid" && !userObj.hasUsageBasedEnabled && isModelPremium(settingsObj.model)) {
                setSettingsObj({model: DEFAULT_MODEL});
            }

            if (!isSubscribed(userObj.subscriptionPlan, userObj.monthlyFreeMessages)) {
                setUnpaidSettings(setSettingsObj);
            }
        }

        if (userObj.remainingBalance < -5) {
            setDynamicStateObj({visiblePopup: "owesOverdueFees"});
        }

        userObjRef.current = userObj;
    }, [userObj]);

    useEffect(() => {
        environmentObjRef.current = environmentObj;
    }, [environmentObj]);

    useEffect(() => {
        const lastApiKeyOnly = settingsObjRef.current?.apiKeyOnly;
        settingsObjRef.current = settingsObj;
        if (settingsObj.autoTheme) {
            const dynamicTheme = getThemeForAuto(environmentObjRef.current?.environment);
            if (dynamicTheme) setSettingsObj({theme: dynamicTheme});
        }
        if (settingsObj.apiKeyOnly && !lastApiKeyOnly) {
            setSettingsObj({model: DEFAULT_MODEL});
        }
    }, [settingsObj]);

    useEffect(() => {
        // If there are now no elements in progress, but there were before
        const lastElementsInProgress = dynamicStateObjRef.current?.elementsInProgress;
        const lastCanSubmit = dynamicStateObjRef.current?.canSubmit;
        dynamicStateObjRef.current = dynamicStateObj;

        if (!isStoppingRef.current) {
            if (dynamicStateObj.elementsInProgress.size === 0 && lastElementsInProgress.size !== 0 && !lastElementsInProgress.has("abort")) {
                if (Object.keys(llmRequestRefs.current?.toolCalls).length !== 0) {
                    llmRequestRefs.current.additionalContext.toolCallAdjustments = {};
                    handleSubmitHelper();
                }
                else {
                    // If there's an error message, mark it as it was aborted so it doesn't decrement from usage counter
                    const wasAborted = dynamicStateObjRef.current.errorMessage;
                    handleEndTurnHelper(wasAborted);
                }
            } else if (dynamicStateObj.elementsInProgress.has('abort') && !lastElementsInProgress.has("abort")) {
                handleEndTurnHelper(true);
            }
        }

        // Process message queue when canSubmit becomes true
        if (dynamicStateObj.canSubmit && !lastCanSubmit) {
            processMessageQueue(dynamicStateObj.messageQueue, updateMessageQueue, messagesRef, currentMessageRef, messagesSequenceRef, settingsObjRef, userObjRef, dynamicStateObjRef, llmRequestRefs, setMessages, updateChatHistory, setDynamicStateObj, updateImportantMessageIds, handleSubmitHelper);
        }

        if (!dynamicStateObj.canSubmit && lastCanSubmit) {
            Object.entries(dynamicStateObjRef.current?.unreviewedFileTracker).forEach(([filePath]) => {
                handleAcceptFile(filePath, environmentObjRef.current?.environment, environmentObjRef.current?.vscode, updateUnreviewedFileTracker);
            });
            setDynamicStateObj({unreviewedFileTracker: {}});
        }
    }, [dynamicStateObj]);

    useEffect(() => {
        let isInitialAuthCheck = true;

        const unsubscribe = auth.onAuthStateChanged((user) => {
            setUserObj({user: user});
            if (!user || !user.emailVerified) {
                setUserObj({subscriptionPlan: null, monthlyFreeMessages: 0});
                setLoggedOutSettings(setSettingsObj);
                setDynamicStateObj({isInitializing: false});
                setEnvironmentObj({projectEmbeddingsLastUpdated: null});

                // Show signed-out popup only on initial auth check, not after logout
                if (isInitialAuthCheck) {
                    setDynamicStateObj({visiblePopup: "signedOut"});
                }
            } else {
                setDynamicStateObj({isInitializing: true});
                getAllChats(user, setDynamicStateObj);
                setupUserState(user, setUserObj, setSettingsObj, setDynamicStateObj, environmentObjRef).then((result: {subscriptionPlan: any, monthlyFreeMessages: any}) => {
                    setDynamicStateObj({isInitializing: false});

                    // Wait 2 sec then setup embeddings if available and usable
                    if (result.subscriptionPlan !== null || result.monthlyFreeMessages > 0) setTimeout(() => checkEmbeddingInfoForProject(user, environmentObjRef, setEnvironmentObj, setDynamicStateObj, dynamicStateObjRef), 2000);
                });
            }

            // Mark that initial auth check is complete
            isInitialAuthCheck = false;
        });

        return () => unsubscribe();
    }, []);

    useEffect(() => {
        let userId = safeLocalStorage.getItem('userId');
        if (!userId) {
            userId = uuidv4();
            safeLocalStorage.setItem('userId', userId);
        }
        setUserObj({fallbackUserId: userId});

        const env = safeLocalStorage.getItem("environment");
        if (env) setEnvironmentObj({environment: env});
        else {
            const isMobileWeb = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            if (isMobileWeb) {
                setEnvironmentObj({environment: 'mobile_web'});
            }
        }

        if (isMobileApp(env)) window.ReactNativeWebView.postMessage(JSON.stringify({functionName: "getRefreshToken", data: []}));

        const vscode = safeGetVscode();
        if (vscode) {
            setEnvironmentObj({vscode: vscode, environment: 'vscode'});
            vscode.postMessage({ functionName: "webViewReady", data: [{ _: "_" }] });
        } else if (env === null) {
            // For Chrome setup. web will be what's set up at this point if its chrome, so only run this if env is null
            window.parent.postMessage({ functionName: 'webViewReady', data: [{ _: "_" }] }, '*');
        }

        // Bridge setup for chrome and VS code. web will be what's set at this point if its chrome, so only run this if env is web or vscode is not null
        if (vscode || env === null) {
            window.addEventListener('message', event => {
                const {functionName, data} = event.data;
                handleMessage(functionName, data, currentMessageRef, setMessages, messagesRef, messagesSequenceRef, setUserObj, setEnvironmentObj, setSettingsObj, setDynamicStateObj, updateElementInProgress, userObjRef, environmentObjRef, settingsObjRef, dynamicStateObjRef, websocketRef, setupWebsocketHelper, miscMidRenderRefs, llmRequestRefs, processSubmitHelper, updateOpenFileStatuses, updateProjectFileBatchIds, updateUnreviewedFileTracker, resetStateHelper, stopRecordingHelper);
            });
        } else if (env.includes("jetbrains") || isMobileApp(env)) {
            window.backendMessage = function (functionName, data) {
                handleMessage(functionName, data, currentMessageRef, setMessages, messagesRef, messagesSequenceRef, setUserObj, setEnvironmentObj, setSettingsObj, setDynamicStateObj, updateElementInProgress, userObjRef, environmentObjRef, settingsObjRef, dynamicStateObjRef, websocketRef, setupWebsocketHelper, miscMidRenderRefs, llmRequestRefs, processSubmitHelper, updateOpenFileStatuses, updateProjectFileBatchIds, updateUnreviewedFileTracker, resetStateHelper, stopRecordingHelper);
            };
        }

        setupWebsocketHelper();
    }, []);

    return (
        <GlobalContext.Provider value={{ websocketRef, messagesRef, currentMessageRef, messagesSequenceRef, menuRef, editorRef, dynamicStateObjRef, userObjRef, settingsObjRef, environmentObjRef, setMessages, llmRequestRefs, miscMidRenderRefs, setupWebsocketHelper, resetStateHelper, handleSubmitHelper, processSubmitHelper, handleEndTurnHelper, stopRecordingHelper }}>
            <div className={`${settingsObj.theme} ${THEME_COLORS} flex flex-col h-dvh ${environmentObj.environment.includes("jetbrains") ? "border-l border-neutral-800" : ""}`}>
                <div ref={menuRef}>
                    <MenuPanel />
                </div>
                <Top isPreview={false}/>
                <div className={`flex flex-col h-dvh overflow-x-scroll`} onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, userObjRef, settingsObjRef, updateSelectedMedia, websocketRef, setupWebsocketHelper, setDynamicStateObj)}>
                    <Middle rootNode={messages} messagesSequence={messagesSequenceRef.current} isPreview={false}/>
                    <Bottom />
                    <Tooltip id="main-tooltip" delayShow={1200} style={{wordBreak: 'break-word', maxWidth: '80vw', padding: '5px', fontSize: '0.875rem', lineHeight: '1.25rem', zIndex: '50'}}/>
                </div>
                <MiddlePopup />
            </div>
        </GlobalContext.Provider>
    );
}

export default App;
